(() => {
  if (customElements.get('yewsea-video-library')) return;

  const readJSON = (root, selector, fallback = null) => {
    try { return JSON.parse(root.querySelector(selector)?.textContent || 'null') ?? fallback; }
    catch { return fallback; }
  };
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = String(text);
    return element;
  };
  const safeURL = (value) => {
    if (!value || typeof value !== 'string') return '';
    try {
      const url = new URL(value, window.location.href);
      return url.protocol === 'https:' || (url.protocol === 'http:' && url.origin === location.origin) ? url.href : '';
    } catch { return ''; }
  };
  const localURL = (value) => {
    const address = safeURL(value);
    if (!address) return '';
    const url = new URL(address);
    return url.origin === location.origin ? url.href : '';
  };
  const youtubeID = (value) => {
    try {
      const url = new URL(String(value || '').trim());
      if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password || url.port) return '';
      const host = url.hostname.toLowerCase(), parts = url.pathname.split('/');
      let id = '';
      if (['youtu.be', 'www.youtu.be'].includes(host)) id = parts[1];
      else if (['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com'].includes(host)) {
        if (['shorts', 'embed', 'live'].includes(parts[1])) id = parts[2];
        else if (/^\/watch\/?$/.test(url.pathname)) id = url.searchParams.get('v');
      }
      return /^[a-zA-Z0-9_-]{11}$/.test(id || '') ? id : '';
    } catch { return ''; }
  };
  let youtubeAPI;
  const loadYouTubeAPI = () => {
    if (window.YT?.Player) return Promise.resolve(window.YT);
    if (youtubeAPI) return youtubeAPI;
    youtubeAPI = new Promise((resolve, reject) => {
      const previous = window.onYouTubeIframeAPIReady;
      const timer = setTimeout(() => reject(new Error('YouTube API unavailable')), 15000);
      window.onYouTubeIframeAPIReady = () => {
        clearTimeout(timer);
        try { previous?.(); } catch {}
        resolve(window.YT);
      };
      const script = node('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.onerror = () => { clearTimeout(timer); reject(new Error('YouTube API unavailable')); };
      document.head.append(script);
    });
    return youtubeAPI;
  };
  const normalizeVideo = (raw) => {
    if (!raw || !raw.handle || !raw.title) return null;
    const id = String(raw.id || '').split('/').pop();
    if (!/^\d+$/.test(id)) return null;
    const sources = (Array.isArray(raw.sources) ? raw.sources : [])
      .filter((source) => source && safeURL(source.url) && /^(video\/mp4|application\/(?:x-mpegurl|vnd\.apple\.mpegurl))$/i.test(source.mimeType))
      .map((source) => ({ ...source, url: safeURL(source.url), height: Number(source.height) || 0 }));
    const youtubeId = raw.source === 'youtube' ? youtubeID(raw.youtubeUrl) : '';
    if ((raw.source === 'youtube' && !youtubeId) || (!youtubeId && !sources.length)) return null;
    return {
      ...raw, id, sources: youtubeId ? [] : sources, source: youtubeId ? 'youtube' : 'shopify', youtubeId,
      youtubeUrl: youtubeId ? 'https://www.youtube.com/watch?v=' + youtubeId : '',
      creator: String(raw.creator || ''), title: String(raw.title), handle: String(raw.handle),
      description: String(raw.description || ''), category: String(raw.category || 'Riding'),
      poster: safeURL(raw.poster), posterSmall: safeURL(raw.posterSmall),
      sortOrder: Number(raw.sortOrder) || 0, duration: Math.max(0, Number(raw.duration) || 0),
      aspectRatio: Number(raw.aspectRatio) || 16 / 9,
      product: raw.product && localURL(raw.product.url) ? raw.product : null
    };
  };
  const ordered = (videos) => videos.slice().sort((a, b) =>
    a.sortOrder - b.sortOrder || (Date.parse(b.publishedAt) || 0) - (Date.parse(a.publishedAt) || 0) || a.title.localeCompare(b.title)
  );
  const validCount = (value) => {
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    if (typeof value === 'string' && !/^\d+$/.test(value)) return null;
    return Number.isSafeInteger(Number(value)) && Number(value) >= 0 ? Number(value) : null;
  };
  const durationLabel = (seconds) => {
    const value = Math.floor(Math.max(0, Number(seconds) || 0));
    return String(Math.floor(value / 60)) + ':' + String(value % 60).padStart(2, '0');
  };
  const createVisitor = () => {
    const key = 'yewsea-video-visitor-v1';
    try {
      const previous = localStorage.getItem(key);
      if (previous && /^[a-zA-Z0-9-]{12,80}$/.test(previous)) return previous;
      const value = crypto.randomUUID();
      localStorage.setItem(key, value);
      return value;
    } catch { return crypto.randomUUID(); }
  };

  class VideoComponent extends HTMLElement {
    start() {
      this.events?.abort();
      this.events = new AbortController();
      this.labels = readJSON(this, '[data-rv-labels]', {});
      this.catalog = readJSON(this, '[data-rv-catalog]', { videos: [], next: null });
      this.videos = ordered((this.catalog.videos || []).map(normalizeVideo).filter(Boolean));
      this.nextURL = this.catalog.next;
      this.stats = new Map();
      this.cards = new Map();
      this.productCards = new Map([...this.querySelectorAll('template[data-rv-product-card]')]
        .map((template) => [localURL(template.dataset.rvProductCard), template]));
      this.numberFormat = new Intl.NumberFormat(document.documentElement.lang || 'en');
      this.interactionMode = this.dataset.rvInteractionMode === 'store' ? 'store' : 'youtube';
      this.endpoint = '';
      const address = localURL(this.dataset.rvEndpoint?.trim());
      if (address && this.interactionMode === 'store') {
        const url = new URL(address);
        if (/^\/apps\/[^/]+/.test(url.pathname) && !url.search && !url.hash) {
          this.endpoint = url.pathname.replace(/\/+$/, '');
        }
      }
      this.visitor = this.endpoint ? createVisitor() : '';
      this.cardTemplate = this.querySelector('[data-rv-card-template]');
    }

    listen(element, event, handler) {
      element?.addEventListener(event, handler, { signal: this.events.signal });
    }

    async request(path, options = {}) {
      if (!this.endpoint) throw new Error('Unavailable');
      const url = new URL(this.endpoint + path, location.origin);
      url.searchParams.set('visitor_id', this.visitor);
      const response = await fetch(url.href, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', ...(options.body ? { 'Content-Type': 'application/json' } : {}) },
        ...options,
        signal: AbortSignal.any([this.events.signal, AbortSignal.timeout(12000)])
      });
      if (!response.ok) {
        const error = new Error('Request failed');
        error.status = response.status;
        throw error;
      }
      const result = await response.json();
      if (!result || typeof result !== 'object') throw new Error('Invalid response');
      return result;
    }

    videoURL(video) {
      const address = localURL(video.url) || localURL(this.dataset.rvDetailUrl);
      if (!address) return '#';
      const url = new URL(address);
      const fallback = new URL(this.dataset.rvDetailUrl || '/pages/ride_videos_detail', location.origin);
      if (url.pathname === fallback.pathname) url.searchParams.set('video', video.handle);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url.pathname + url.search;
    }

    async completeCatalog() {
      const visited = new Set();
      while (this.nextURL && this.isConnected) {
        const address = localURL(this.nextURL);
        if (!address || visited.has(address)) throw new Error('Invalid pagination');
        visited.add(address);
        const url = new URL(address);
        url.searchParams.set('section_id', this.dataset.rvSection);
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url.href, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: AbortSignal.any([this.events.signal, AbortSignal.timeout(12000)])
        });
        if (!response.ok) throw new Error('Catalog request failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const data = readJSON(doc, '[data-rv-catalog]');
        if (!data || !Array.isArray(data.videos)) throw new Error('Invalid catalog');
        doc.querySelectorAll('template[data-rv-product-card]').forEach((template) => {
          const address = localURL(template.dataset.rvProductCard);
          if (address) this.productCards.set(address, template);
        });
        const known = new Set(this.videos.map((video) => video.id));
        for (const raw of data.videos) {
          const video = normalizeVideo(raw);
          if (video && !known.has(video.id)) { this.videos.push(video); known.add(video.id); }
        }
        this.videos = ordered(this.videos);
        this.nextURL = data.next;
      }
    }

    async fetchStats(videos) {
      if (!this.endpoint || !videos.length) return false;
      for (let index = 0; index < videos.length; index += 40) {
        const ids = videos.slice(index, index + 40).map((video) => video.id).join(',');
        const result = await this.request('/stats?ids=' + encodeURIComponent(ids));
        if (!result.stats || typeof result.stats !== 'object') throw new Error('Invalid statistics');
        for (const [id, stats] of Object.entries(result.stats)) {
          if (!stats || !['views', 'likes'].every((key) => validCount(stats[key]) !== null)) continue;
          this.stats.set(String(id).split('/').pop(), {
            views: validCount(stats.views), likes: validCount(stats.likes), 
            liked: stats.liked === true
          });
        }
      }
      return true;
    }

    paintStats(root, video) {
      const data = this.stats.get(video.id);
      root.querySelectorAll('[data-rv-count]').forEach((element) => {
        const value = data?.[element.dataset.rvCount];
        element.textContent = value === undefined ? '—' : this.numberFormat.format(value);
      });
      const stats = root.matches('[data-rv-stats]') ? root : root.querySelector('[data-rv-stats]');
      if (stats) stats.hidden = !data;
      stats?.setAttribute('aria-label', data
        ? [data.views + ' ' + this.labels.views, data.likes + ' ' + this.labels.likes].join(', ')
        : this.labels.statsUnavailable);
    }

    createCard(video) {
      const card = this.cardTemplate.content.firstElementChild.cloneNode(true);
      card.dataset.rvId = video.id;
      card.dataset.rvHandle = video.handle;
      card.dataset.rvSource = video.source;
      const link = card.querySelector('[data-rv-card-link]');
      link.href = this.videoURL(video);
      link.setAttribute('aria-label', this.labels.playNamed.replace('__title__', video.title));
      link.classList.toggle('rv-card__poster--portrait', video.source !== 'youtube' && video.aspectRatio < 1);
      const title = card.querySelector('[data-rv-card-title]');
      title.textContent = video.title;
      title.href = link.href;
      const imageAddress = video.posterSmall || video.poster;
      if (imageAddress) {
        card.querySelector('[data-rv-poster-placeholder]')?.remove();
        const image = card.querySelector('.rv-card__image') || node('img', 'rv-card__image');
        image.src = imageAddress;
        image.alt = video.title;
        image.width = 720;
        image.height = Math.round(720 / video.aspectRatio);
        image.loading = 'lazy';
        image.decoding = 'async';
        if (!image.isConnected) link.prepend(image);
      }
      const duration = card.querySelector('[data-rv-duration]');
      duration.textContent = durationLabel(video.duration);
      duration.hidden = !video.duration;
      const creator = card.querySelector('[data-rv-creator]');
      creator.textContent = video.creator; creator.hidden = !video.creator;
      card.querySelector('[data-rv-platform]').hidden = video.source !== 'youtube';
      this.configureCardActions(card, video);
      const product = card.querySelector('[data-rv-card-product]');
      product.hidden = !video.product;
      if (video.product) {
        product.href = localURL(video.product.url);
        product.querySelector('[data-rv-card-product-name]').textContent = video.product.title;
      }
      this.paintStats(card, video);
      return card;
    }

    configureCardActions(card, video) {
      card.querySelector('[data-rv-youtube-actions]').hidden = video.source !== 'youtube' || this.interactionMode === 'store';
      for (const [selector, allowed] of [['[data-rv-youtube-like]', video.allowLikes]]) {
        const action = card.querySelector(selector);
        action.href = video.youtubeUrl || '#';
        action.hidden = !allowed;
      }
    }

    installCardActions(inline = false) {
      this.listen(this, 'click', (event) => {
        const target = event.target.closest('[data-rv-card-share], [data-rv-card-link]');
        const card = target?.closest('[data-rv-card]');
        if (!card || !this.contains(card)) return;
        const video = this.videos.find((item) => item.id === String(card.dataset.rvId).split('/').pop());
        if (!video) return;
        if (target.matches('[data-rv-card-share]')) { event.preventDefault(); this.shareVideo(video); }
        else if (inline && video.source === 'youtube' && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
          event.preventDefault();
          this.stopInline();
          const stage = card.querySelector('[data-rv-inline-stage]');
          this.activeInline = { stage, poster: target, id: video.id };
          this.mountYouTube(stage, video);
        }
      });
    }

    stopInline() {
      if (!this.activeInline) return;
      this.activeInline.stage.replaceChildren(this.activeInline.poster);
      this.activeInline = null;
    }

    mountYouTube(stage, video) {
      const iframe = node('iframe', 'rv-youtube-frame');
      const url = new URL('https://www.youtube-nocookie.com/embed/' + video.youtubeId);
      for (const [key, value] of Object.entries({ autoplay: '1', playsinline: '1', rel: '0', enablejsapi: '1', origin: location.origin })) url.searchParams.set(key, value);
      iframe.title = video.title;
      iframe.src = url.href;
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      stage.replaceChildren(iframe);
      iframe.focus();
      return iframe;
    }

    async shareVideo(video) {
      const address = new URL(this.videoURL(video), location.origin);
      address.searchParams.delete('preview_theme_id');
      if (navigator.share) {
        try { await navigator.share({ title: video.title, url: address.href }); return; }
        catch (error) { if (error.name === 'AbortError') return; }
      }
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(address.href);
        this.status.textContent = this.labels.copied;
      } catch { this.status.textContent = this.labels.shareError + ' ' + address.href; }
    }

    disconnectedCallback() {
      this.stopInline();
      this.events?.abort();
      this.querySelector('video')?.pause();
      clearInterval(this.playerPoll);
      this.youtubePlayer?.destroy();
    }
  }

  class VideoLibrary extends VideoComponent {
    connectedCallback() {
      this.start();
      this.grid = this.querySelector('[data-rv-grid]');
      this.empty = this.querySelector('[data-rv-empty]');
      this.more = this.querySelector('[data-rv-more]');
      this.status = this.querySelector('[data-rv-status]');
      this.search = this.querySelector('[data-rv-search]');
      this.category = 'all';
      this.installCardActions(true);
      this.limit = Math.max(6, Number(this.dataset.rvPageSize) || 12);
      this.pageSize = this.limit;
      this.querySelectorAll('[data-rv-grid] > [data-rv-card]').forEach((card) => {
        this.cards.set(String(card.dataset.rvId).split('/').pop(), card);
      });
      this.listen(this.querySelector('[data-rv-search-form]'), 'submit', (event) => { event.preventDefault(); this.render(); });
      this.listen(this.search, 'input', () => { this.limit = this.pageSize; this.render(); });
      this.querySelectorAll('[data-rv-category]').forEach((button) => this.listen(button, 'click', () => {
        this.category = button.dataset.rvCategory;
        this.limit = this.pageSize;
        this.querySelectorAll('[data-rv-category]').forEach((tab) => {
          const active = tab === button;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-pressed', String(active));
        });
        this.render();
      }));
      this.listen(this.more, 'click', () => { this.limit += this.pageSize; this.render(); });
      this.dataset.rvReady = 'true';
      this.render();
      this.load();
    }

    async load() {
      try { await this.completeCatalog(); if (!this.isConnected) return; this.render(); }
      catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) {
          this.status.textContent = this.labels.loadError;
          this.querySelector('[data-rv-native-pagination]')?.removeAttribute('hidden');
          this.dataset.rvCatalogPartial = 'true';
        }
      }
      try { await this.fetchStats(this.videos); if (this.isConnected) this.render(); } catch {}
    }

    render() {
      const query = String(this.search.value || '').trim().toLocaleLowerCase();
      const matches = this.videos.filter((video) => (this.category === 'all' || video.category === this.category)
        && (!query || [video.title, video.description, video.product?.title || ''].join(' ').toLocaleLowerCase().includes(query)));
      const visible = matches.slice(0, this.limit);
      if (this.activeInline && !visible.some((video) => video.id === this.activeInline.id)) this.stopInline();
      const nodes = visible.map((video) => {
        let card = this.cards.get(video.id);
        if (!card) { card = this.createCard(video); this.cards.set(video.id, card); }
        card.querySelectorAll('[data-rv-card-link], [data-rv-card-title]').forEach((link) => { link.href = this.videoURL(video); });
        this.configureCardActions(card, video);
        this.paintStats(card, video);
        return card;
      });
      const visibleSet = new Set(nodes);
      [...this.grid.children].forEach((child) => { if (child.matches('[data-rv-card]') && !visibleSet.has(child)) child.remove(); });
      nodes.forEach((card, index) => {
        const atIndex = this.grid.querySelectorAll(':scope > [data-rv-card]')[index];
        if (atIndex !== card) this.grid.insertBefore(card, atIndex || this.grid.querySelector('[data-rv-native-pagination]'));
      });
      this.empty.hidden = matches.length > 0;
      this.empty.querySelector('[data-rv-empty-title]').textContent = this.videos.length ? this.labels.noResults : this.labels.empty;
      this.more.hidden = visible.length >= matches.length;
      this.querySelector('[data-rv-results]').textContent = this.labels.results.replace('__count__', this.numberFormat.format(matches.length));
    }
  }

  class VideoWatch extends VideoComponent {
    connectedCallback() {
      this.start();
      this.status = this.querySelector('[data-rv-status]');
      this.likeButton = this.querySelector('[data-rv-like]');
      this.launchTemplate = this.querySelector('[data-rv-start]').cloneNode(true);
      this.installCardActions();
      this.revision = 0;
      const selected = normalizeVideo(readJSON(this, '[data-rv-selected]'));
      if (selected && !this.videos.some((video) => video.id === selected.id)) this.videos.push(selected);
      this.listen(this.likeButton, 'click', () => this.toggleLike());
      this.listen(this.querySelector('[data-rv-share]'), 'click', () => this.share());
      this.load(selected);
    }

    async load(selected) {
      const requested = new URL(location.href).searchParams.get('video');
      let current = requested ? this.videos.find((video) => video.handle === requested) : selected || this.videos[0];
      if (!current && requested && this.nextURL) {
        try { await this.completeCatalog(); current = this.videos.find((video) => video.handle === requested); }
        catch { this.status.textContent = this.labels.loadError; }
      }
      if (!this.isConnected) return;
      if (!current) {
        this.querySelector('[data-rv-watch-content]').hidden = true;
        this.querySelector('[data-rv-not-found]').hidden = false;
        return;
      }
      this.setCurrent(current, selected?.id === current.id);
      try { await this.completeCatalog(); if (this.isConnected) this.renderRelated(); } catch {}
      if (!this.endpoint) return;
      try {
        await this.fetchStats([current, ...this.videos.filter((v) => v.id !== current.id).slice(0, 3)]);
        if (!this.isConnected) return;
        this.paintCurrentStats();
        this.renderRelated();
      } catch {}
    }

    setCurrent(video, keepPoster) {
      clearInterval(this.playerPoll);
      this.youtubePlayer?.destroy();
      this.youtubePlayer = null;
      this.current = video;
      this.dataset.rvSource = video.source;
      this.revision += 1;
      this.viewSubmitted = false;
      this.viewEventId = crypto.randomUUID();
      this.viewAttempts = 0;
      this.nextViewAttempt = 0;
      this.querySelector('[data-rv-watch-content]').hidden = false;
      this.querySelector('[data-rv-not-found]').hidden = true;
      this.querySelector('[data-rv-title]').textContent = video.title;
      this.querySelector('[data-rv-description]').textContent = video.description;
      this.dataset.rvPortrait = String(video.aspectRatio < 1);
      const stage = this.querySelector('[data-rv-stage]');
      if (!keepPoster) {
        stage.replaceChildren();
        if (video.poster) {
          const image = node('img', 'rv-player__poster');
          image.src = video.poster;
          image.alt = video.title;
          image.width = 1600;
          image.height = Math.round(1600 / video.aspectRatio);
          image.fetchPriority = 'high';
          stage.append(image);
        }
      }
      let start = stage.querySelector('[data-rv-start]');
      if (!start) { start = this.launchTemplate.cloneNode(true); stage.append(start); }
      start.hidden = false;
      this.listen(start, 'click', () => this.play());
      this.querySelector('.rv-watch__metrics').hidden = !this.endpoint;
      const nativeActions = video.source === 'youtube' && this.interactionMode === 'youtube';
      this.querySelectorAll('[data-rv-youtube-like], [data-rv-youtube-watch]').forEach((action) => {
        action.href = video.youtubeUrl || '#';
        action.hidden = video.source !== 'youtube'
          || (action.matches('[data-rv-youtube-like]') && (!nativeActions || !video.allowLikes));
      });
      this.likeButton.hidden = video.allowLikes !== true;
      this.likeButton.disabled = true;
      this.updateProduct(video.product);
      this.renderRelated();
      document.title = video.title + ' | YEWSEA';
    }

    play() {
      const stage = this.querySelector('[data-rv-stage]');
      if (stage.querySelector('video, iframe')) return;
      if (this.current.source === 'youtube') {
        const iframe = this.mountYouTube(stage, this.current);
        if (this.endpoint) this.trackYouTube(iframe);
        return;
      }
      const video = node('video');
      video.controls = true;
      video.playsInline = true;
      video.preload = 'none';
      video.setAttribute('aria-label', this.current.title);
      if (this.current.poster) video.poster = this.current.poster;
      const nativeHLS = video.canPlayType('application/vnd.apple.mpegurl');
      const saveData = navigator.connection?.saveData === true;
      const sources = this.current.sources.slice().sort((a, b) => {
        const aHLS = /mpegurl/i.test(a.mimeType), bHLS = /mpegurl/i.test(b.mimeType);
        if (aHLS !== bHLS) return nativeHLS ? Number(bHLS) - Number(aHLS) : Number(aHLS) - Number(bHLS);
        return saveData ? a.height - b.height : b.height - a.height;
      });
      for (const source of sources) {
        const element = node('source');
        element.src = source.url;
        element.type = source.mimeType;
        video.append(element);
      }
      this.listen(video, 'timeupdate', () => this.recordView(video));
      this.listen(video, 'ended', () => this.recordView(video));
      this.listen(video, 'error', () => { this.status.textContent = this.labels.loadError; });
      stage.replaceChildren(video);
      video.play().catch(() => {});
    }

    async recordView(player) {
      let played = 0;
      for (let index = 0; index < player.played.length; index += 1) played += player.played.end(index) - player.played.start(index);
      await this.recordWatched(played, Number.isFinite(player.duration) ? player.duration : this.current.duration);
    }

    async trackYouTube(iframe) {
      const revision = this.revision;
      try {
        const api = await loadYouTubeAPI();
        if (!this.isConnected || revision !== this.revision || !iframe.isConnected) return;
        let played = 0, previous = 0;
        this.youtubePlayer = new api.Player(iframe, { events: {
          onStateChange: (event) => {
            clearInterval(this.playerPoll);
            if (event.data !== api.PlayerState.PLAYING) return;
            previous = event.target.getCurrentTime();
            this.playerPoll = setInterval(() => {
              if (!this.isConnected || revision !== this.revision) { clearInterval(this.playerPoll); return; }
              const now = event.target.getCurrentTime(), delta = now - previous;
              if (delta > 0 && delta < 2.5) played += delta;
              previous = now;
              this.recordWatched(played, event.target.getDuration());
            }, 1000);
          },
          onError: () => { this.status.textContent = this.labels.loadError; }
        } });
      } catch {}
    }

    async recordWatched(played, duration) {
      if (!this.endpoint || this.viewSubmitted || this.viewAttempts >= 3 || Date.now() < this.nextViewAttempt) return;
      if (played < Math.min(10, (duration || 20) / 2)) return;
      this.viewSubmitted = true;
      this.viewAttempts += 1;
      try {
        const data = await this.request('/videos/' + encodeURIComponent(this.current.id) + '/views', {
          method: 'POST', body: JSON.stringify({ eventId: this.viewEventId, watchedSeconds: Math.floor(played) })
        });
        if (data.stats) { this.acceptStats(data.stats); this.paintCurrentStats(); }
      } catch { this.viewSubmitted = false; this.nextViewAttempt = Date.now() + 30000; }
    }

    acceptStats(data) {
      if (data && ['views', 'likes'].every((key) => validCount(data[key]) !== null)) {
        this.stats.set(this.current.id, {
          views: validCount(data.views), likes: validCount(data.likes),  liked: data.liked === true
        });
      }
    }

    paintCurrentStats() {
      this.paintStats(this.querySelector('.rv-watch__metrics'), this.current);
      const stats = this.stats.get(this.current.id);
      this.likeButton.disabled = !stats || this.current.allowLikes !== true;
      this.likeButton.setAttribute('aria-pressed', String(stats?.liked === true));
    }

    async toggleLike() {
      if (this.likeButton.disabled || !this.current.allowLikes) return;
      this.likeButton.disabled = true;
      const desired = this.stats.get(this.current.id)?.liked !== true;
      try {
        const data = await this.request('/videos/' + encodeURIComponent(this.current.id) + '/likes', {
          method: 'POST', body: JSON.stringify({ liked: desired })
        });
        if (!data.stats) throw new Error('Invalid statistics');
        this.acceptStats(data.stats);
        this.status.textContent = '';
      } catch { this.status.textContent = this.labels.likeError; }
      finally { if (this.isConnected) this.paintCurrentStats(); }
    }

    updateProduct(product) {
      const box = this.querySelector('[data-rv-featured-product]');
      const fallback = this.querySelector('[data-rv-fallback-products]');
      const template = product && this.productCards.get(localURL(product.url));
      box.hidden = !template;
      fallback.hidden = Boolean(template) || !fallback.querySelector('.yh-collection-card');
      const grid = box.querySelector('[data-rv-product-grid]');
      grid.replaceChildren(...(template ? [template.content.cloneNode(true)] : []));
    }

    renderRelated() {
      if (!this.current) return;
      const target = this.querySelector('[data-rv-related]');
      target.replaceChildren(...ordered(this.videos).filter((video) => video.id !== this.current.id).slice(0, 3).map((video) => this.createCard(video)));
      target.parentElement.hidden = !target.children.length;
      target.closest('.rv-watch__aside').hidden = !target.children.length;
    }

    async share() {
      await this.shareVideo(this.current);
    }
  }

  customElements.define('yewsea-video-library', VideoLibrary);
  customElements.define('yewsea-video-watch', VideoWatch);
})();
